import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { LoaderIcon, Upload, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import z from 'zod';
import { STYLE_OPTIONS } from '../../constants/data';
import { ImageToImageApis } from '../../services/imageApis';
import ExternalTools from './ExternalTools';
import ApplyChangesDialog from './ApplyChangesDialog';
import { saveFile, saveGeneratedImage, setFileName } from 'store/slices/imageToImage';
import { fileToUrl } from 'api/commonApi';

const schema = z.object({
  style: z.string(),
});

type TSchema = z.infer<typeof schema>;

export default function EditImagePanel() {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // New state for overall processing
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Local state for the image being edited
  const [localImage, setLocalImage] = useState<{
    file: File | null;
    preview: string | null;
    name: string | null;
  }>({
    file: null,
    preview: null,
    name: null
  });
  
  // Flag to track if we're showing the upload panel
  const [showUploadPanel, setShowUploadPanel] = useState(false);

  const dispatch = useDispatch();

  const { 
    isSaved, 
    generatedImageUrl, 
    selectedStyle, 
    originalImageInfo, 
    originalImageBase64,
    fileName 
  } = useSelector((state: RootState) => state.imageToImage);
  
  // Helper function to convert base64 to File
  const base64ToFile = (base64: string, fileName: string, type: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || type;
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };
  
  // Initialize local state with global state on mount
  useEffect(() => {
    if (generatedImageUrl && !localImage.preview && !showUploadPanel) {
      setLocalImage({
        file: originalImageBase64 ? base64ToFile(
          originalImageBase64, 
          originalImageInfo?.name || 'image', 
          originalImageInfo?.type || 'image/jpeg'
        ) : null,
        preview: generatedImageUrl,
        name: fileName
      });
    }
  }, [generatedImageUrl, fileName, originalImageBase64, originalImageInfo, localImage.preview, showUploadPanel]);

  const MAX_FILE_SIZE_MB = 5;

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return null;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPEG)');
      return null;
    }

    setError(null);
    // Update local state
    setLocalImage({
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name
    });
    
    setShowUploadPanel(false);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateFile(file);
    }
  };

  const clearSelectedImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Show upload panel instead of clearing global state
    setShowUploadPanel(true);
    setLocalImage({
      file: null,
      preview: null,
      name: null
    });
  };

  // Mutation for non-Ghibli styles (image-to-image API)
  const { mutate: mutateStyle, isPending: isStylePending } = useMutation({
    mutationKey: ['generate-styled-image'],
    mutationFn: async (params: { image_url: string; feature_name: string }) => {
      return ImageToImageApis.imageToImage(params);
    },
    onSuccess: (data) => {
      dispatch(
        saveGeneratedImage({
          generatedImageUrl: data.data,
          selectedStyle: form.getValues('style'),
        })
      );
      
      // If we used a new local image, update the file name
      if (localImage.file && localImage.name) {
        dispatch(setFileName({ fileName: localImage.name }));
      }
      
      dispatch(saveFile({ fileSaved: false }));
      setIsAlertDialogOpen(false);
      setIsProcessing(false); // Clear processing state on success
      toast.success('Style applied successfully!');
    },
    onError: (error) => {
      console.error('Error:', error);
      toast.error('Something went wrong!');
      setIsProcessing(false); // Clear processing state on error
    },
  });

  // Mutation for Ghibli style (ghibli-image API)
  const { mutate: mutateGhibli, isPending: isGhibliPending } = useMutation({
    mutationKey: ['generate-ghibli-image-edit'],
    mutationFn: async (params: { image: File }) => {
      const formData = new FormData();
      formData.append('image', params.image);
      return ImageToImageApis.generateGhibliImage(formData);
    },
    onSuccess: (data) => {
      dispatch(
        saveGeneratedImage({
          generatedImageUrl: data.data,
          selectedStyle: 'ghibli',
        })
      );
      
      // If we used a new local image, update the file name
      if (localImage.file && localImage.name) {
        dispatch(setFileName({ fileName: localImage.name }));
      }
      
      dispatch(saveFile({ fileSaved: false }));
      setIsAlertDialogOpen(false);
      setIsProcessing(false); // Clear processing state on success
      toast.success('Style applied successfully!');
    },
    onError: (error) => {
      console.error('Error:', error);
      toast.error('Something went wrong!');
      setIsProcessing(false); // Clear processing state on error
    },
  });

  // Use isProcessing instead of isPending to show loading state
  const isPending = isProcessing || isStylePending || isGhibliPending;

  const form = useForm<TSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      style: selectedStyle,
    },
  });

  const handleContinueWithoutSaving = async () => {
    setIsAlertDialogOpen(false);
    
    // Set processing state immediately
    setIsProcessing(true);
    
    const selectedStyleValue = form.getValues('style');
    
    // Check if we have a local image file
    if (!localImage.file) {
      toast.error('No image selected');
      setIsProcessing(false);
      return;
    }
    
    try {
      // Handle Ghibli style differently
      if (selectedStyleValue === 'ghibli') {
        mutateGhibli({ image: localImage.file });
      } else {
        // For other styles, use the image-to-image API
        const fileUrlResponse = await fileToUrl(localImage.file);
        mutateStyle({
          image_url: fileUrlResponse.data.url,
          feature_name: selectedStyleValue
        });
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleApplyChanges = async () => {
    if (isSaved) {
      // Set processing state immediately
      setIsProcessing(true);
      
      const selectedStyleValue = form.getValues('style');
      
      // Check if we have a local image file
      if (!localImage.file) {
        toast.error('No image selected');
        setIsProcessing(false);
        return;
      }
      
      try {
        // Handle Ghibli style differently
        if (selectedStyleValue === 'ghibli') {
          mutateGhibli({ image: localImage.file });
        } else {
          // For other styles, use the image-to-image API
          const fileUrlResponse = await fileToUrl(localImage.file);
          mutateStyle({
            image_url: fileUrlResponse.data.url,
            feature_name: selectedStyleValue
          });
        }
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error('Failed to process image. Please try again.');
        setIsProcessing(false);
      }
    } else {
      setIsAlertDialogOpen(true);
    }
  };

  return (
    <div className="bg-background-main text-text-primary flex flex-col rounded-md lg:p-3 lg:px-4 col-span-3 overflow-y-auto overflow-x-hidden">
      <p className="text-primary uppercase text-sm">Edit Image</p>

      <form className="mt-3 flex flex-1 flex-col gap-4 lg:px-0">
        {/* Upload Image Section */}
        <div>
          {!showUploadPanel && localImage.preview ? (
            // Uploaded Image Tile
            <div className="flex items-center gap-3 p-2 border rounded-lg">
              <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0">
                <img
                  src={localImage.preview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="flex-1 text-sm font-medium truncate">
                {localImage.name || fileName}
              </span>
              <button
                onClick={clearSelectedImage}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            // Upload Panel
            <div
              className={cn(
                "relative flex items-center justify-center h-[52px] border-2 rounded-lg border-dashed transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-primary hover:border-primary/50 cursor-pointer"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                <p className="text-sm font-medium text-text-primary">
                  Upload image
                </p>
                <span className="text-xs text-gray-500">
                  (PNG, JPEG up to 5MB)
                </span>
              </div>
            </div>
          )}
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>

        {/* Style Selection Section */}
        <div className="space-y-4">
          <label className="text-sm font-medium">Change style</label>
          <Controller
            control={form.control}
            name="style"
            render={({ field }) => (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3">
                {STYLE_OPTIONS.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    className={cn(
                      'relative aspect-square rounded-lg overflow-hidden border-2 transition-all',
                      field.value === style.id 
                        ? 'border-primary ring-2 ring-primary ring-opacity-50' 
                        : 'border-transparent hover:border-primary/50'
                    )}
                    onClick={() => field.onChange(style.id)}
                  >
                    <img 
                      src={style.imageUrl} 
                      alt={style.label}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white py-1.5 px-2 text-xs">
                      {style.label}
                    </div>
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        {/* Apply Buttons */}
        <div className="w-full mt-auto">
          {isSaved ? (
            <Button
              disabled={(!form.formState.isDirty && !localImage.file) || isPending}
              className="w-full text-lg"
              variant="customPrimary"
              onClick={handleApplyChanges}
            >
              {isPending && (
                <LoaderIcon className="w-4 h-4 animate-spin mr-2" />
              )}
              {isPending ? 'Applying' : 'Apply'}
            </Button>
          ) : (
            <ApplyChangesDialog
              isOpen={isAlertDialogOpen}
              setIsOpen={setIsAlertDialogOpen}
              isPending={isPending}
              isSaved={isSaved}
              handleContinueWithoutSaving={handleContinueWithoutSaving}
              formState={form.formState}
            />
          )}
        </div>
      </form>

      <div className="hidden lg:block">
        <ExternalTools />
      </div>
    </div>
  );
} 